/**
 * **墨与量纲 —— 控件层的设计资产。**
 *
 * ⚠️ 这个文件**刻意不 import Pixi**（只用 `palette.ts` / `theme.ts` 的色值），
 * 因为规范里那几条最要紧的规矩要能在 node 里**当成数据**验：
 *
 * - hover 只加墨：`STAMP_INK` 的 `hover` 只许写"从无到有"的项
 * - 禁用不用透明度：`DISABLED_INK` 里根本没有 alpha 这个字段
 *
 * 文字/描边的换算需要 `TextStyle`，那部分在 `style.ts`。
 */

import { BASE, LIGHT, SIGNAL, STROKE, mixHex } from '../palette';


/**
 * 控件的墨色。
 *
 * ⚠️ 底色一律是**纯黑或近黑**，需要"跳出来"的才用酸色原样 ——
 * 大面积铺酸色会把黑底毁掉（`visual-design.md` §2.2 的 85/12/3 用量规则）。
 */
export const INK = {
  /** 面板底：近黑，抬起来一档 */
  panel: BASE.raised,
  /** 面板描边：收过饱和的线色，不是纯白 —— 白描边会在黑底上刺眼 */
  edge: STROKE.neutral,
  /** 更强的分隔线 / 当前焦点框 */
  edgeStrong: STROKE.accent,
  /** 正文 */
  text: LIGHT.base,
  /** 次要文字 */
  textDim: LIGHT.dim,
  /** 反白块（knockout）的底与字 —— 构成主义最省力的一个动作 */
  knockoutFill: LIGHT.base,
  onKnockout: BASE.void,
  /** 主按钮 */
  primary: SIGNAL.accent,
  /** 危险（踢人 / 重置） */
  danger: SIGNAL.danger,
  /** 强调 / 选中 */
  accent: SIGNAL.accent,
  /** 等待响应：酸青。**断点线用这个**（`ui-design.md` §6） */
  waiting: SIGNAL.info,
  /** 警告（游戏规则有问题） */
  warning: SIGNAL.warning,
  /** 在线 / 已就绪 */
  ok: SIGNAL.ok,
  /** 分隔线 / 禁用描边 / 注销杠。**不许当强调色用** */
  rule: STROKE.neutral,
  /**
   * **长按确认那条从左到右的条。**
   *
   * 用**纸色**：它扫过去的地方是"这一块被重新印了一遍纸"。在酸玫红的
   * 危险章上，纸白是唯一还读得出来的一档（章面是玫红、字是黑，
   * 再来一个黑条就和字糊在一起了）。
   */
  holdBar: LIGHT.base,
  /** 遮罩层。**必须够黑**，否则底下的界面会透出来干扰阅读 */
  scrim: BASE.scrim,
} as const;

/** 字距：英文全大写拉 1.4 倍是设计规则里"把机读这件事变成视觉"的做法 */
export const TRACKING = {
  /** 页眉、版号、编号 */
  wide: 2.4,
  /** 按钮、标签 */
  normal: 0.8,
  /** 正文不拉 */
  none: 0,
} as const;

/** 描边粗细（设计尺寸，由调用方按倍率换算） */
export const LINE = {
  /** 普通描边 */
  hair: 1,
  /** 分隔线 */
  rule: 1,
  /** 焦点框 / 主按钮描边 —— 印刷品的边是**粗**的 */
  bold: 2,
} as const;

/**
 * **间距只有三档。**
 *
 * ⚠️ 这是 `ui-design.md` §3.3 那条"一切长度相对一个锚"的落点之一。
 * 允许调用点手写 `gap: 7` 的话，版面会在几十次微调之后变成一堆
 * 谁也不挨着谁的数字 —— 而那是**屏幕语言**里最难查的一种（它不报错）。
 */
export const GAP = {
  /** 同一块内容里，行与行 */
  tight: 6,
  /** 并列的控件之间 */
  normal: 12,
  /** 分区之间 */
  loose: 20,
} as const;

// ============================================================
// 印章（可交互控件的唯一底座）
// ============================================================

/**
 * **印章的四种变体。不许再加第五种。**
 *
 * 「按钮是印章 / 贴纸，不是圆角矩形」（`visual-design.md` §8），
 * 所以变体的差别只在于**用哪几块墨**，不在于形状。
 *
 * | 变体 | 是什么 | 用在哪 |
 * | --- | --- | --- |
 * | `primary` | 酸色实心章 + 反白字 | 这一屏**唯一**的主动作 |
 * | `secondary` | 纸底 + 酸色描边 + 酸色字 | 其余动作 |
 * | `quiet` | 只有 `dim` 字，无块无框 | 返回、次级入口、dev 入口 |
 * | `danger` | 酸玫红实心章 + 反白字 | 破坏性动作（踢人、离开） |
 *
 * ⚠️ **一屏最多一个 `primary`。** 两个实心章并排等于没有主次，
 * 而那正是"统一模板 + 换图标"那种工具感的来源。
 */
export type StampVariant = 'primary' | 'secondary' | 'quiet' | 'danger';

/**
 * 一块印章在某一个状态下的墨。
 *
 * ⚠️ 三个字段**都是必填的**。早先 `fill` / `edge` 允许是 `null`（"这块不印"），
 * 但加了过渡之后 `null` 没法插值 —— 而"没有描边"完全可以用
 * **描边色 = 底色**表达（`quiet` 就是这么做的）。少一个 `null`
 * 分支，过渡路径上就少一处"这里要不要特判"。
 */
export interface StampInk {
  readonly fill: number;
  readonly edge: number;
  readonly label: number;
}

/**
 * 变体 → 墨。
 *
 * `hover` 只写**变化的那几项** —— 因为 hover 的其余部分由"多印一版"
 * 负责（`state.ts` 的 `drawStamp`），不需要也不允许在这里另配一套配色。
 * 这是"hover 一律加墨"在数据结构上的体现：**想减墨都没有地方写**。
 */
export const STAMP_INK: Record<
  StampVariant,
  { readonly idle: StampInk; readonly hover: Partial<StampInk> }
> = {
  primary: {
    idle: { fill: INK.primary, edge: INK.primary, label: INK.onKnockout },
    hover: {},
  },
  danger: {
    idle: { fill: INK.danger, edge: INK.danger, label: INK.onKnockout },
    hover: {},
  },
  secondary: {
    idle: { fill: INK.panel, edge: INK.primary, label: INK.primary },
    hover: {},
  },
  quiet: {
    // 一块没着墨的暗底 + 一行暗字。**描边色 = 底色**，所以看上去没有框
    //（描边是"这是个主控件"的信号，次级入口不该有）。
    //
    // ⚠️ 底不能省。省掉的版本实测过：菜单是画在**活着的半调背景**上的，
    // 一块没有底的文字会被白点吃掉，读起来是"这里坏了"而不是"这是次级入口"。
    idle: { fill: INK.panel, edge: INK.panel, label: INK.textDim },
    hover: { edge: INK.rule, label: INK.text },
  },
};

/**
 * 禁用时的墨：**撤墨**。
 *
 * ⚠️ 这里**不是** `alpha: 0.35`。`visual-design.md` §6 写着
 * 「降透明度是 App 语言，涂黑是印刷语言」，还加了「最后一条特别重要」。
 * `ui-design.md` §2.3 记了为什么最终落成"撤墨 + 注销杠"而不是"黑条盖字"。
 */
export const DISABLED_INK: StampInk = {
  fill: BASE.void,
  /**
   * ⚠️ 描边也撤掉。
   *
   * 第一版这里用的是 `STROKE.neutral`（那条淡蓝的线色），实测下来
   * **一个禁用的按钮和旁边正常按钮一样显眼** —— 因为它借到了一条
   * "这是个控件"的线，而禁用第一件该做到的事就是**退到后面去**。
   * 所以描边退成 `BASE.sunken`（近黑的一档），由**注销杠**独自承担
   * "这块不许用"这件事。
   */
  edge: BASE.sunken,
  label: LIGHT.dim,
};

/**
 * 某一变体在某一状态下的最终墨色。
 *
 * ⚠️ 它是**唯一**决定配色的地方 —— 组件不许自己算颜色。
 * 这个函数放在这里（而不是 `state.ts`）是为了让它可测：
 * "hover 只加墨"这条规矩可以拿它当数据验（见 `test/ui/design.test.ts`）。
 */
export function stampInk(variant: StampVariant, state: ControlStateName): StampInk {
  if (state === 'disabled') return DISABLED_INK;
  const base = STAMP_INK[variant];
  // ⚠️ 只有 `hover` 拿那份额外的墨。`press` 与 `idle` 用**同一套**墨 ——
  // 按下的差别只在位置（整体下沉 1px）。写成"非 idle 就加 hover"
  // 会让按下的那一刻突然多出一圈描边，那是错的（测试抓出来的）
  if (state === 'hover') return { ...base.idle, ...base.hover };
  return base.idle;
}

/** 控件状态。真正的状态机在 `state.ts`，这里只用它的名字 */
export type ControlStateName = 'idle' | 'hover' | 'press' | 'disabled';

// ============================================================
// 位移：hover 抬起来，press 压下去
// ============================================================

/**
 * **hover 抬起的高度**（设计像素）。
 *
 * 评审：「hover 效果是加印……手感太烂了，应当改为向上移动」。
 * "加印一版"（套印错位）在纸上是对的，但在**屏幕**上它只是"块大了一圈"——
 * 而鼠标掠过控件时人期待的是"这块东西被拿起来了"。
 *
 * ⚠️ 只有 2px。§6 那条「位移：小。印刷品的运动是**压**，不是**飞**」
 * 在这里同样是硬约束 —— 抬 8px 会变成"悬浮卡片"，那是 App 语言。
 */
export const LIFT = 2;

/** press 下沉的高度。和抬起同一个量级：按下去的感觉是"压回纸面" */
export const PRESS_SINK = 1;

/**
 * 某个状态的目标位移（**负值向上**）。
 *
 * 这条是纯数据，所以 `test/ui/design.test.ts` 能直接断言方向：
 * hover 必须向上、press 必须向下、idle / disabled 必须不动。
 */
export function stampLift(state: ControlStateName): number {
  switch (state) {
    case 'hover':
      return -LIFT;
    case 'press':
      return PRESS_SINK;
    default:
      return 0;
  }
}

/**
 * 某一变体在某一状态下、**过渡进度为 `mix` 时**的墨。
 *
 * `mix` 是 0..1 的补间进度（`motion.ts` 给的）。
 * ⚠️ `disabled` 不走插值 —— "撤墨 + 注销杠"是一个**动作**，
 * 渐变过去反而像"慢慢淡出"，那是 App 的语义。
 */
export function stampInkAt(variant: StampVariant, state: ControlStateName, mix: number): StampInk {
  if (state === 'disabled') return DISABLED_INK;
  const idle = STAMP_INK[variant].idle;
  const hover = stampInk(variant, 'hover');
  if (mix <= 0) return idle;
  if (mix >= 1) return hover;
  return {
    fill: mixHex(idle.fill, hover.fill, mix),
    edge: mixHex(idle.edge, hover.edge, mix),
    label: mixHex(idle.label, hover.label, mix),
  };
}
